import React, { Fragment, useEffect, useState } from "react";
import { ReactComponent as SearchIcon } from "assets/images/common/svgs/search.svg";
import { ReactComponent as DeleteIcon } from "assets/images/common/svgs/deleteIconTable.svg";
import { ReactComponent as EyeIcon } from "assets/images/common/svgs/eyeIconTable.svg";
import { useNavigate } from "react-router-dom";
import { siteRoutes } from "utils/helpers/enums/routes.enum";
import { UserDTO } from "utils/helpers/models/users/user.dto";
import useExperiencesEvents from "../useHook";
import { useSelector } from "react-redux";
import {
  ExperienceFiltersDTO,
  UserFiltersDTO,
} from "utils/helpers/models/common/user-filters.dto";
import { useDebounce } from "hooks/useDebounce";
import { confirmationPopup } from "utils/helpers/common/alert-service";
import DataNotFound from "components/particles/table/data-not-found";
import Pagination from "components/particles/table/pagination";
import NextSessionDate from "components/NextSession";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";
import editProfilePicUser from "assets/images/common/others/editProfileUSer.png";
import useStore from "hooks/useStore";
import { sitePermissions } from "utils/helpers/enums/permissions.enum";

const ExperiencesListing: React.FC = () => {
  const columns: string[] = [
    "Images",
    "Title",
    "Location",
    "Date",
    "Experience Type",
    "Status",
    "Action",
  ];

  const navigate = useNavigate();
  const { hasPermission } = useStore();
  const [data, setData] = useState<UserDTO[]>([]);
  const { getAllEvents } = useExperiencesEvents();
  const { isLoading } = useSelector((state: any) => state.sharedReducer);
  const [totalElements, setTotalElements] = useState<number>(0);

  const [filters, setFilters] = useState<ExperienceFiltersDTO>(() => ({

    ...new ExperienceFiltersDTO(),
    page: 1,
    limit: 10,
    searchTitle: "",
    isSession: "",
  }));

  const debounceSearch = useDebounce(filters.searchTitle, 1000);

  const onPageChange = (pageInfo: { selected: number; limit: number }) => {
    const { selected: page, limit } = pageInfo;
    filters.size = limit;
    filters.page = page + 1;
    setFilters({ ...filters });
    getAllData(filters);
  };

  const getAllData = (filters: ExperienceFiltersDTO) => {
    const filtersToSend: any = {};
    for (let key in filters) {
      if (filters[key]) filtersToSend[key] = filters[key];
    }
    const queryParams = {
      ...filtersToSend,
      page: filters.page - 1,
    };
    getAllEvents(setData, queryParams, setFilters, setTotalElements);
  };

  useEffect(() => {
    getAllData(filters);
  }, [debounceSearch]);

  const handleChangeFilter = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;

    // Handle "isSession" value as string for select dropdown
    if (name === "isSession") {
      setFilters({ ...filters, [name]: value === "" ? "" : value });
    } else {
      setFilters({ ...filters, [name]: value });
    }

    if (name === "searchTitle" && value === "") {
      getAllData(filters);
    }
  };


  const resetFilters = () => {
    const model = new ExperienceFiltersDTO();
    for (let key in model) {
      filters[key] = model[key];
    }

    setFilters({ ...filters });
    getAllData(filters);
  };

  const handleSearch = (event: any) => {
    const { key } = event;
    if (key === "Enter") {
      getAllData(filters);
    }
  };

  const goToEventDetail = (id: number) => {
    navigate(`${siteRoutes.viewExperienceDetail}?id=${id}`);
  };

  return (
    <div className="">
      <h1 className="tex-2xl sm:text-[32px] text-primaryText font-bold mb-6">
        Experience
      </h1>
      <div className="flex  justify-end items-center gap-5 ">
        <div className="w-full sm:w-[388px] relative">
          {/* this is key in my api on the base of this search  name="searchTitle" */}
          <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 text-primaryText text-lg" />
          <input
            type="search"
            placeholder="Search by title"
            value={filters.searchTitle}
            onChange={handleChangeFilter}
            onKeyUp={handleSearch}
            name="searchTitle"
            className="w-full pl-10 p-2 border-[0.6px] border-borderLightGray rounded-full focus:outline-none bg-coolLightGrayBg"
          />
        </div>
      </div>
      <div className="bg-coolLightGrayBg border  border-borderLightGray p-4 rounded-lg w-full my-6">
        <div className="w-full overflow-x-auto scrollbar-hidden">
          <div className="flex flex-wrap sm:flex-nowrap lg:justify-end items-center gap-3">
            {/* isSession this key on the base of this key filter use  */}
            <select
              value={filters.isSession}
              onChange={handleChangeFilter}
              name="isSession"
              className="px-3 border border-lightGrayBorder text-primaryText outline-none rounded-lg text-sm bg-white h-[46px] w-full sm:w-auto min-w-[140px]"
            >
              <option value="">Experience Type</option>
              <option value="false">Workshop</option>
              <option value="true">Individual Session</option>
            </select>
            {/* timeFrame this is key  */}
            <div className="flex flex-col">
              <input
                type="date"
                placeholder="  Select Date"
                className="px-3 border border-lightGrayBorder text-primaryText outline-none rounded-lg text-sm bg-white h-[46px] w-full sm:w-auto min-w-[140px]"
              />
            </div>

            <button
              onClick={() => getAllData(filters)}
              className="px-4 py-2 bg-darkTealBlue text-white rounded-lg text-sm h-[46px] w-full md:w-auto min-w-[140px]"
            >
              Apply Filters
            </button>

            {/* Clear Filters Button */}
            <button
              onClick={resetFilters}
              className="px-4 py-2 bg-techGlowBg text-primaryText rounded-lg text-sm h-[46px] w-full md:w-auto min-w-[140px]"
            >
              Clear Filter
            </button>
          </div>
        </div>
      </div>

      <div className="w-full flex flex-col gap-4">
        <div className="bg-white rounded-t-lg  border border-mediumGrayBorder overflow-x-auto">
          <table className="min-w-full divide-y divide-mediumGrayBorder">
            {/* Table Header */}
            <thead className="bg-white">
              <tr className="text-left">
                {columns.map((column, index) => {
                  return (
                    <th
                      className="px-4 py-4 text-sm font-bold text-left text-primaryText whitespace-nowrap"
                      key={index}
                    >
                      {column}
                    </th>
                  );
                })}
              </tr>
            </thead>

            {/* Table Body */}
            <tbody className="bg-white divide-y divide-mediumGrayBorder">
              {data.map((exp: any, index) => (
                <tr key={index} className="hover:bg-gray-50 ">
                  <td className="px-4 py-4">
                    <img
                      className="rounded-full w-9 h-9 object-cover"
                      src={
                        exp?.eventPics[0]?.name
                          ? concatPathWithBackendUrl(exp?.eventPics[0]?.name)
                          : editProfilePicUser
                      }
                      alt={exp.Profile}
                    />
                  </td>

                  <td className="px-4 py-4 text-sm  text-primaryText font-semibold whitespace-nowrap">
                    {exp?.title}
                  </td>
                  <td className="px-4 py-4 text-sm  text-primaryText font-semibold whitespace-nowrap">
                    {exp?.location}
                  </td>
                  <td className="px-4 py-4 text-sm text-primaryText font-semibold whitespace-nowrap">
                    <NextSessionDate
                      workshopSessions={exp?.workshopSessions || []}
                    />
                  </td>

                  <td className="px-4 py-4 text-sm text-primaryText font-semibold whitespace-nowrap">
                    {exp?.isSession ? "Session" : "Workshop"}
                  </td>

                  <td className="px-4 py-4 text-sm font-semibold whitespace-nowrap">
                    {exp?.eventStatus}
                  </td>

                  <td className="px-4 py-4 text-left whitespace-nowrap w-[124px]">
                    <div className="flex justify-start gap-2 ">

                      {
                        hasPermission(sitePermissions.experiencesRead) ? (
                          <button
                            onClick={() => goToEventDetail(exp?.id)}
                            className=""
                          >
                            <EyeIcon />
                          </button>
                        ) : (
                          <span className="cursor-not-allowed">
                            <EyeIcon />
                          </span>
                        )}
                    </div>
                  </td>


                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Fragment>
          <DataNotFound show={!isLoading && !data.length} />
          <Pagination
            count={totalElements}
            page={filters.page}
            limit={filters.limit}
            onPageChange={onPageChange}
          />
        </Fragment>
      </div>
    </div>
  );
};

export default ExperiencesListing;
